/**
 * Org structure — the chart stage.
 *
 * Renders one of the three chart shapes over a pannable, zoomable plane.
 *
 * Picking a unit reduces the plane to that unit's line of command — the map
 * from the top of the tree, through it, to everything under it — and the page
 * puts the unit's details up as a popup over the top. The details deliberately
 * do NOT live in here: anything absolutely positioned inside the stage bubbles
 * its pointer and wheel events straight back into pan and zoom.
 *
 * Pan and zoom are deliberately kept out of React state: the transform lives in
 * a ref and is written straight to the layer's style. Dragging a chart of a few
 * hundred cards through a re-render per pointermove is the one thing that makes
 * this feel slow, and nothing else on screen depends on the transform.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from './org-icons';
import { layoutBounds, layoutChart, wirePath, type ChartLayout, type ChartView, type PlacedNode } from './org-layout';
import { avatarStyle, initials, headColour, headLabel, lineageOf, matches, nf, vacancyPct, vacancyTone, peopleLabel, type OrgModel, type OrgNode } from './org-model';
import styles from './org-structure.module.css';

/*
 * The zoom floor is 50%, not 14%.
 *
 * Below about a quarter there is no honest way to draw a card: 272px becomes 68
 * physical pixels, which is roughly two characters of readable type, so the
 * chart used to fall back to plain coloured blocks. Blocks carry depth and
 * nothing else — you could see the shape of the force and not one fact about
 * it, which is what made zooming out feel broken rather than compact.
 *
 * So the floor sits where a card can still say something. At 50% the brief tier
 * applies and the type is scaled back up to about 11px on screen: the unit's
 * name and its figures stay readable at every zoom the chart will go to. Seeing
 * more of a large force is a matter of panning now, not of shrinking it past
 * the point where it stops being information.
 */
const MIN_Z = 0.5;
const MAX_Z = 2;

/**
 * The zoom the chart opens at.
 *
 * Fixed rather than fitted. Fitting means the opening zoom depends on how wide
 * the force happens to be, so the same screen lands at 60% for one project and
 * 11% for another — and at 11% a card carries no readable text at all. A
 * constant is predictable: every project opens at the same size, and the top
 * two levels are what is drawn, so there is something worth reading there.
 */
const HOME_Z = 0.5;

/**
 * How much a card can say, given how much room it has on screen.
 *
 * A 272px card at 30% zoom is 82 physical pixels wide — about seven characters
 * of readable text. Rendering the same six lines at every zoom is why a fitted
 * chart came out as an unreadable postage stamp: the type shrank with the box
 * until it was two pixels tall. So the card drops content as it shrinks, and
 * what survives is scaled back UP to stay legible.
 *
 * Thresholds come from the arithmetic: characters across ≈ 49·z, lines down
 * ≈ 11·z, both at an 11px floor for readable type.
 */
type Detail = 'full' | 'brief' | 'mini' | 'block';

const detailFor = (z: number): Detail =>
  z >= 0.6 ? 'full' : z >= 0.32 ? 'brief' : z >= 0.25 ? 'mini' : 'block';

/**
 * How far to scale a card's type back up, capped.
 *
 * Targets roughly 11 physical pixels — the floor for comfortable reading — for
 * type authored at 14. Scaling by a full 1/z overshoots that, and every pixel
 * of overshoot is content pushed out of a box whose size is fixed by the
 * layout, so the figures row ends up clipped away.
 *
 * The 3.2 cap is what sets the block threshold above: past 0.8/3.2 = 25% zoom
 * the scale can no longer keep type above the floor, so there is no honest way
 * to draw a name and the tier below takes over.
 */
const lodFor = (z: number) => Math.min(3.2, Math.max(1, 0.8 / z));

/**
 * The spacing of the drafting grid behind the chart, in plane units.
 *
 * Stepped rather than fixed, so the grid never becomes noise at a low zoom or
 * a bare field at a high one: the step climbs and falls by fives until its
 * spacing on screen sits in a comfortable band. This is why the grid reads as
 * paper the chart is laid on rather than as a texture stuck to the glass.
 */
const gridStep = (z: number) => {
  let s = 24;
  while (s * z < 18) s *= 5;
  while (s * z > 90) s /= 5;
  return s;
};


interface ChartProps {
  model: OrgModel;
  view: ChartView;
  collapsed: Set<string>;
  onToggleCollapse: (id: string) => void;
  selected: string | null;
  onSelect: (id: string | null) => void;
  query: string;
  vacantOnly: boolean;
  /**
   * Draw ONLY the selected unit's line of command — its ancestors, itself and
   * everything under it. Off draws the whole tree with the selection merely
   * marked.
   */
  isolate: boolean;
  compareSet: string[];
  onToggleCompare: (id: string) => void;
  /**
   * Bumped by the page when the chart should re-frame itself wholesale — a
   * different chart shape, a different depth, a new project. Deliberately NOT
   * bumped when a single branch is folded or unfolded: re-fitting on every
   * toggle throws away the zoom and pan you had set up.
   */
  fitToken: number;
  /**
   * How the next `fitToken` re-frame should look:
   *   'fit'  — scale the visible tree to fill the plane. Used on load, reset and
   *            project switch, where only the top couple of levels show, so the
   *            fit stays readable and the chart no longer opens tiny in a corner.
   *   'home' — the fixed opening zoom, top of the tree pinned near the top. Used
   *            when revealing deeper levels, so a big tree is never shrunk to fit.
   * Defaults to 'home' to preserve the original behaviour when omitted.
   */
  framingMode?: 'fit' | 'home';
  /** One-shot "frame this unit's whole line of command". */
  focus?: { id: string; token: number } | null;
  /** Show the first-run orientation card; the callback dismisses it for good. */
  hint?: () => void;
  /** Re-open the details popup for whatever is selected. */
  onShowDetails?: () => void;
}

const Avatar = ({ node }: { node: OrgNode }) => (
  <span
    className={styles.av}
    style={avatarStyle(node)}
    aria-hidden="true"
  >
    {node.head ? initials(node.head.name) : '—'}
  </span>
);

const OrgChart = ({
  model,
  view,
  collapsed,
  onToggleCollapse,
  selected,
  onSelect,
  query,
  vacantOnly,
  isolate,
  compareSet,
  onToggleCompare,
  fitToken,
  framingMode = 'home',
  focus,
  hint,
  onShowDetails,
}: ChartProps) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const tf = useRef({ z: 1, px: 0, py: 0 });
  const [zoomLabel, setZoomLabel] = useState('100%');
  // Coarse enough that panning never re-renders: it only changes when a
  // threshold is crossed, and React bails out on an identical value.
  const [detail, setDetail] = useState<Detail>('full');
  /** What the last programmatic framing was, so a resize can reapply it. */
  const framing = useRef<{ kind: 'home' } | { kind: 'fit' } | { kind: 'focus'; id: string }>({ kind: 'home' });
  /** Set the moment the user pans or zooms — after that the camera is theirs. */
  const touched = useRef(false);
  /**
   * The unit the keyboard is on.
   *
   * Every card used to be a tab stop: at "Every level" on a real force that is
   * a couple of thousand sequential stops with no way to skip a subtree, and
   * the stage itself was not focusable at all — so a chart whose primary
   * gesture is panning could not be panned from a keyboard.
   *
   * One stop, and the arrows move within it: the pattern a tree view uses.
   */
  const [cursor, setCursor] = useState<string | null>(null);

  /**
   * The line of command to draw on its own, or null for the whole tree.
   *
   * This is the layout input, not a paint-time flag: everything outside the
   * line is never placed, so the plane collapses to the line and Fit frames it
   * at a zoom where the cards can be read.
   */
  const only = useMemo(
    () => (isolate && selected && model.byId[selected] ? lineageOf(model, selected) : null),
    [isolate, selected, model]
  );

  /**
   * An isolated line is always drawn stacked, whichever shape the tree is in.
   *
   * The shape control is about reading a whole tree. Once it is one line, the
   * only thing that can be wide is the row of units reporting to the chosen
   * one, and fanning those out across the plane is what makes them unreadable:
   * a district with twenty stations is 6,000px across fanned and fits at 19%
   * zoom — the tier where a card is a coloured block with no name on it. The
   * same twenty stacked beneath their district fit at 42%, with names, officers
   * and figures on every row.
   */
  const shape: ChartView = only ? 'compact' : view;

  const layout: ChartLayout = useMemo(
    () => layoutChart(model, shape, collapsed, only),
    [model, shape, collapsed, only]
  );

  const apply = useCallback(() => {
    const l = layerRef.current;
    if (!l) return;
    const { z, px, py } = tf.current;
    l.style.transform = `translate(${px}px,${py}px) scale(${z})`;
    l.style.setProperty('--lod', String(lodFor(z)));
    // The grid is drawn on the stage, not on the moving layer, so it has to be
    // told where the plane has got to. Anchored to the plane's own origin, it
    // slides under the cards as you pan and breathes as you zoom.
    const st = stageRef.current;
    if (st) {
      const g = gridStep(z);
      st.style.setProperty('--org-gs', `${g * z}px`);
      st.style.setProperty('--org-gs2', `${g * 5 * z}px`);
      st.style.setProperty('--org-gx', `${px}px`);
      st.style.setProperty('--org-gy', `${py}px`);
    }
    setZoomLabel(`${Math.round(z * 100)}%`);
    setDetail(detailFor(z));
  }, []);

  /**
   * The opening camera: a fixed 50%, top of the tree centred and near the top
   * edge, which is where the eye starts on an org chart.
   */
  const home = useCallback(() => {
    const st = stageRef.current;
    if (!st) return;
    const r = st.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const b = layoutBounds(layout);
    tf.current = {
      z: HOME_Z,
      px: (r.width - (b.x1 - b.x0) * HOME_Z) / 2 - b.x0 * HOME_Z,
      py: 16 - b.y0 * HOME_Z,
    };
    framing.current = { kind: 'home' };
    touched.current = false;
    apply();
  }, [layout, apply]);

  const fit = useCallback(() => {
    const st = stageRef.current;
    if (!st) return;
    const b = layoutBounds(layout);
    const r = st.getBoundingClientRect();
    if (!r.width || !r.height) return;
    // The banner floats over the top-left of the plane, so a line fitted to the
    // full height puts the top of that line — the thing the banner is naming —
    // underneath it. Fit into what is actually clear.
    const top = selected ? 52 : 0;
    const h = Math.max(120, r.height - top);
    const z = Math.max(MIN_Z, Math.min(r.width / (b.x1 - b.x0), h / (b.y1 - b.y0), 1.2) * 0.94);
    tf.current = {
      z,
      px: (r.width - (b.x1 - b.x0) * z) / 2 - b.x0 * z,
      py: top + (h - (b.y1 - b.y0) * z) / 2 - b.y0 * z,
    };
    framing.current = { kind: 'fit' };
    touched.current = false;
    apply();
  }, [layout, apply, selected]);

  /**
   * Frame the unit, what reports to it, and the one command above it.
   *
   * Centring alone was not enough: it left the zoom wherever it happened to be,
   * so a search result could land correctly centred but unreadably small.
   *
   * The frame deliberately stops at the immediate parent rather than the root.
   * The whole ancestor chain is drawn, but including it in the box stretches it over every level of the org, which
   * pushes the zoom back down to roughly what fitting the entire chart gives.
   * One level of context reads; six levels of it is just the whole chart again.
   */
  const focusOn = useCallback(
    (id: string) => {
      // Once the chart is reduced to one line of command, the plane IS the
      // answer — framing "this unit and one level above" would push the top of
      // the line off screen, and the top is half of what was asked for.
      if (only) {
        fit();
        return;
      }
      const st = stageRef.current;
      const u = model.byId[id];
      if (!st || !u) return;
      const flow = [u.parentId, id, ...model.descendants(id)]
        .filter((x): x is string => !!x)
        .map((x) => layout.nodes[x])
        .filter(Boolean);
      if (!flow.length) return;
      const pad = 48;
      const x0 = Math.min(...flow.map((n) => n.x)) - pad;
      const y0 = Math.min(...flow.map((n) => n.y)) - pad;
      const x1 = Math.max(...flow.map((n) => n.x + n.w)) + pad;
      const y1 = Math.max(...flow.map((n) => n.y + n.h)) + pad;
      const r = st.getBoundingClientRect();
      if (!r.width || !r.height) return;
      // Capped above 1 so a single station does not balloon to 300%.
      const z = Math.max(MIN_Z, Math.min(r.width / (x1 - x0), r.height / (y1 - y0), 1.1));
      tf.current = { z, px: (r.width - (x1 - x0) * z) / 2 - x0 * z, py: (r.height - (y1 - y0) * z) / 2 - y0 * z };
      framing.current = { kind: 'focus', id };
      touched.current = false;
      apply();
    },
    [only, fit, layout, model, apply]
  );

  /*
   * Re-frame only when the page asks, keyed on the token alone.
   *
   * Depending on `layout` here instead would mean any unrelated collapse toggle
   * re-ran the effect and silently undid the focus. So the camera moves are
   * called through a ref holding the current closures — and because effects run
   * in declaration order, this one has to come first, so the token effects
   * below see the versions built from the layout that raised the token.
   */
  const latest = useRef({ home, fit, focusOn, focus });
  useEffect(() => {
    latest.current = { home, fit, focusOn, focus };
  });

  // Assigned in the render body (not an effect) so the token effect below reads
  // the mode that came in with this same fitToken bump — the two props update
  // together on the page, so by commit this ref already holds the new mode.
  const framingModeRef = useRef(framingMode);
  framingModeRef.current = framingMode;

  useEffect(() => {
    if (framingModeRef.current === 'fit') latest.current.fit();
    else latest.current.home();
  }, [fitToken]);

  const focusToken = focus?.token ?? -1;
  useEffect(() => {
    const want = latest.current.focus;
    if (want) latest.current.focusOn(want.id);
  }, [focusToken]);

  /*
   * Reapply the last framing when the canvas changes size — going full screen,
   * opening the summary rail, resizing the window. Without this the chart keeps
   * the zoom it had for a small box and sits in the corner of a big one.
   *
   * Only while the camera is still ours: once you have panned or zoomed,
   * a resize must not throw that away. The observer also fires once on observe,
   * which covers a mount that happened before the container had a size.
   */
  useEffect(() => {
    const st = stageRef.current;
    if (!st || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (touched.current) return;
      const f = framing.current;
      if (f.kind === 'focus') latest.current.focusOn(f.id);
      else if (f.kind === 'fit') latest.current.fit();
      else latest.current.home();
    });
    ro.observe(st);
    return () => ro.disconnect();
  }, []);

  /* ── keyboard: roving focus over the drawn nodes ── */
  /** Only what is actually on the plane, in reading order. */
  const walkable = useMemo(() => {
    const drawn = Object.values(layout.nodes);
    return drawn
      .slice()
      .sort((a, b) => a.depth - b.depth || a.slot - b.slot || a.x - b.x)
      .map((n) => n.id);
  }, [layout]);

  const here = cursor && layout.nodes[cursor] ? cursor : walkable[0] || null;

  /** Siblings that are drawn, in slot order — what left and right move over. */
  const peersOf = useCallback(
    (id: string) => {
      const p = model.byId[id]?.parentId;
      const sibs = (p && model.byId[p] ? model.byId[p].kids : model.roots).filter((k) => layout.nodes[k]);
      return sibs.length ? sibs : [id];
    },
    [layout, model]
  );

  const moveTo = useCallback(
    (id: string | undefined) => {
      if (!id || !layout.nodes[id]) return;
      setCursor(id);
      // Bring it into view without disturbing the zoom the user chose.
      const st = stageRef.current;
      const n = layout.nodes[id];
      if (!st) return;
      const r = st.getBoundingClientRect();
      const { z, px, py } = tf.current;
      const sx = n.x * z + px;
      const sy = n.y * z + py;
      const pad = 24;
      let dx = 0;
      let dy = 0;
      if (sx < pad) dx = pad - sx;
      else if (sx + n.w * z > r.width - pad) dx = r.width - pad - (sx + n.w * z);
      if (sy < pad) dy = pad - sy;
      else if (sy + n.h * z > r.height - pad) dy = r.height - pad - (sy + n.h * z);
      if (!dx && !dy) return;
      touched.current = true;
      tf.current = { z, px: px + dx, py: py + dy };
      apply();
    },
    [layout, apply]
  );

  const onStageKey = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest(`.${styles.overlay}`)) return;
    const PAN = 90;
    const id = here;
    // Shift turns the arrows into the pan the chart never had from a keyboard.
    if (e.shiftKey && e.key.startsWith('Arrow')) {
      e.preventDefault();
      touched.current = true;
      const d = { ArrowLeft: [PAN, 0], ArrowRight: [-PAN, 0], ArrowUp: [0, PAN], ArrowDown: [0, -PAN] }[e.key];
      if (!d) return;
      tf.current = { ...tf.current, px: tf.current.px + d[0], py: tf.current.py + d[1] };
      apply();
      return;
    }
    if (!id) return;
    const u = model.byId[id];
    const sibs = peersOf(id);
    const i = sibs.indexOf(id);
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        moveTo(sibs[Math.min(sibs.length - 1, i + 1)]);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        moveTo(sibs[Math.max(0, i - 1)]);
        break;
      case 'ArrowDown': {
        e.preventDefault();
        const kid = u.kids.find((k) => layout.nodes[k]);
        moveTo(kid);
        break;
      }
      case 'ArrowUp':
        e.preventDefault();
        moveTo(u.parentId && layout.nodes[u.parentId] ? u.parentId : undefined);
        break;
      case 'Home':
        e.preventDefault();
        moveTo(walkable[0]);
        break;
      case 'End':
        e.preventDefault();
        moveTo(walkable[walkable.length - 1]);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(id);
        break;
      case '+':
      case '-':
        if (u.kids.length) {
          e.preventDefault();
          onToggleCollapse(id);
        }
        break;
      default:
    }
  };

  /* ── pan and zoom ── */
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  /** True while the last gesture was a drag, so releasing over a card does not select it. */
  const dragged = useRef(false);

  /**
   * Pointer capture is taken only once the pointer has actually MOVED.
   *
   * Capturing on pointerdown retargets the following `click` to the stage, so a
   * card's own click handler never runs — which is why selecting a unit did
   * nothing. Waiting for real movement means a plain click never involves
   * capture at all, and it also stops a one-pixel wobble from counting as a
   * drag that suppresses the selection.
   */
  const DRAG_SLOP = 4;

  /**
   * The chrome floating over the plane — the banner, the zoom box, the details
   * popup — is not the plane.
   *
   * Everything absolutely positioned inside the stage still bubbles its pointer
   * and wheel events to it, so dragging a panel's scrollbar panned the chart and
   * a wheel over a panel hit the stage's `preventDefault` and zoomed instead of
   * scrolling. That is why the screen went dead after a card was clicked: the
   * details panel could neither be scrolled nor clicked through.
   */
  const overlay = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.(`.${styles.overlay}`);

  const onPointerDown = (e: React.PointerEvent) => {
    if (overlay(e.target) || (e.target as HTMLElement).closest('button')) return;
    dragged.current = false;
    drag.current = { x: e.clientX, y: e.clientY, px: tf.current.px, py: tf.current.py };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const far = Math.abs(e.clientX - d.x) > DRAG_SLOP || Math.abs(e.clientY - d.y) > DRAG_SLOP;
    if (!dragged.current) {
      if (!far) return;
      dragged.current = true;
      stageRef.current?.classList.add(styles.drag);
      stageRef.current?.setPointerCapture(e.pointerId);
    }
    touched.current = true;
    tf.current = { ...tf.current, px: d.px + (e.clientX - d.x), py: d.py + (e.clientY - d.y) };
    apply();
  };
  const endDrag = () => {
    drag.current = null;
    stageRef.current?.classList.remove(styles.drag);
  };

  // Wheel is bound by hand: React's onWheel is passive, so preventDefault on it
  // is ignored and the page scrolls behind the chart while you zoom.
  useEffect(() => {
    const st = stageRef.current;
    if (!st) return;
    const onWheel = (e: WheelEvent) => {
      if (overlay(e.target)) return;
      e.preventDefault();
      const r = st.getBoundingClientRect();
      const mx = e.clientX - r.left;
      const my = e.clientY - r.top;
      const { z, px, py } = tf.current;
      touched.current = true;
      const nz = Math.min(MAX_Z, Math.max(MIN_Z, z * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
      tf.current = { z: nz, px: mx - (mx - px) * (nz / z), py: my - (my - py) * (nz / z) };
      apply();
    };
    st.addEventListener('wheel', onWheel, { passive: false });
    return () => st.removeEventListener('wheel', onWheel);
  }, [apply]);

  const zoomBy = (factor: number) => {
    touched.current = true;
    const z = Math.min(MAX_Z, Math.max(MIN_Z, tf.current.z * factor));
    tf.current = { ...tf.current, z };
    apply();
  };

  /**
   * Select, unless the pointer was released at the end of a drag.
   *
   * Picking the unit that is already selected re-selects it rather than
   * clearing it: the caller reads that as "show me this one again", which puts
   * the details popup back if it was dismissed. Clearing has its own control on
   * the banner, and Escape.
   */
  const choose = (id: string) => {
    if (dragged.current) return;
    setCursor(id);
    onSelect(id);
  };
  const lit = useMemo(
    () => new Set(selected && model.byId[selected] ? [selected, ...model.byId[selected].path] : []),
    [selected, model]
  );
  /**
   * Dimming only means something when something is left sharp. A query that
   * matches nothing would otherwise fade the entire chart to 25%, which reads
   * as a rendering fault rather than an empty result — and the result list has
   * already said "no matches" in plain words.
   */
  const filtering = useMemo(() => {
    if (!query && !vacantOnly) return false;
    return model.ids.some((id) => matches(model.byId[id], query, vacantOnly));
  }, [model, query, vacantOnly]);
  const stored = (id: string) => compareSet.includes(id);

  const pin = (id: string) => (
    <button
      type="button"
      className={`${styles.pin}${stored(id) ? ` ${styles.pinon}` : ''}`}
      title={stored(id) ? 'Remove from the compare set' : 'Add to the compare set'}
      aria-label={stored(id) ? 'Remove from the compare set' : 'Add to the compare set'}
      onClick={(e) => {
        e.stopPropagation();
        onToggleCompare(id);
      }}
    >
      {stored(id) ? '✓' : '+'}
    </button>
  );

  const card = (n: PlacedNode) => {
    const u = model.byId[n.id];
    const vac = u.headState === 'vacant';
    const dim = filtering && !matches(u, query, vacantOnly);
    const tone = headColour(u);
    // `key` is deliberately NOT in here: React warns when a key is spread in
    // through a props object rather than written on the element.
    const common = {
      role: 'treeitem',
      'aria-level': u.lvl + 1,
      'aria-selected': selected === u.id,
      'aria-expanded': u.kids.length ? !collapsed.has(u.id) : undefined,
      id: `orgnode-${n.id}`,
      style: { left: n.x, top: n.y, width: n.w, height: n.h },
      onClick: () => choose(u.id),
    };
    const cls =
      `${styles.nd}${vac ? ` ${styles.vac}` : ''}${u.isActive ? '' : ` ${styles.dormant}`}` +
      `${selected === u.id ? ` ${styles.sel}` : ''}${dim ? ` ${styles.dim}` : ''}` +
      `${vacantOnly && vac ? ` ${styles.flag}` : ''}${here === u.id ? ` ${styles.cursor}` : ''}`;

    /* Too small for any type at all. A solid block still carries the two things
       worth knowing at this distance: where the unit sits in the hierarchy, by
       colour, and whether anyone is in charge of it. */
    if (detail === 'block') {
      return (
        <div
          key={n.id}
          {...common}
          className={`${cls} ${styles.lodblock}`}
          style={{ ...common.style, background: tone }}
          aria-label={u.name}
          title={`${u.name} · ${u.typeLabel}`}
        />
      );
    }

    /* Room for a name and nothing else. */
    if (detail === 'mini') {
      return (
        <div key={n.id}
          {...common} className={`${cls} ${styles.lodmini}`} title={`${u.name} · ${u.typeLabel}`}>
          <span className={styles.strip} style={{ background: tone }} />
          <span className={styles.mininame}>{u.name}</span>
        </div>
      );
    }

    const brief = detail === 'brief';
    return (
      <div
        key={n.id}
          {...common}
        className={`${cls} ${styles.lodz}${brief ? ` ${styles.lodbrief}` : ''}`}
        title={brief ? `${u.name} · ${u.typeLabel}` : undefined}
      >
        <span className={styles.strip} style={{ background: tone }} />
        {pin(u.id)}
        <span className={styles.ndb}>
          <span className={styles.uname}>{u.name}</span>
          {!brief && (
            <span className={styles.ucode}>
              {u.code} &nbsp;·&nbsp; {u.typeLabel.toUpperCase()}
              {!u.isActive && <span className={styles.inactive}>Inactive</span>}
            </span>
          )}
          <span className={styles.hr} />
          {/* Name and post on separate lines. On one line they fought over 272px
              and both lost: the badge truncated to two characters and a post
              like "Director General of Police" came out as "Director G…". The
              badge is dropped here entirely — it is in the inspector, and it is
              the least useful of the three on a card. */}
          <span className={styles.who}>
            <Avatar node={u} />
            <span className={styles.whotx}>
              <span className={styles.oname}>{headLabel(u)}</span>
              {!brief && u.headRank && (
                <span className={styles.orank} title={u.headRank}>
                  {u.headRank}
                </span>
              )}
            </span>
          </span>
        </span>
        {/* Small: one plain sentence rather than caps labels. Compensated type
            renders at 0.8x whatever it is authored at, so a 10px label lands at
            8px however hard it is scaled — the one size no adjustment rescues.
            At full size the labelled figures are back. */}
        <span className={styles.figrow}>
          {brief ? (
            <span className={styles.fbrief}>
              {u.kids.length ? `${nf(u.kids.length)} report here` : 'Last level'}
            </span>
          ) : (
            <>
              <span className={styles.f}>
                <b>People</b>
                <span>{peopleLabel(model, u)}</span>
              </span>
              <span className={styles.f}>
                <b>Unfilled</b>
                {/* No posts on record is not the same as fully staffed. */}
                <span style={{ color: vacancyTone(vacancyPct(u)) }}>
                  {vacancyPct(u) === null ? '—' : `${vacancyPct(u)}%`}
                </span>
              </span>
              <span className={styles.f}>
                <b>Sub-units</b>
                <span>{u.subUnits ? nf(u.subUnits) : '—'}</span>
              </span>
            </>
          )}
        </span>
      </div>
    );
  };

  const leafRow = (n: PlacedNode) => {
    const u = model.byId[n.id];
    const vac = u.headState === 'vacant';
    const dim = filtering && !matches(u, query, vacantOnly);
    return (
      <div
        key={n.id}
        role="treeitem"
        aria-level={u.lvl + 1}
        aria-selected={selected === u.id}
        id={`orgnode-${n.id}`}
        className={
          `${styles.leaf}${vac ? ` ${styles.vac}` : ''}${detail !== 'full' ? ` ${styles.lodbrief}` : ''}` +
          `${selected === u.id ? ` ${styles.sel}` : ''}${dim ? ` ${styles.dim}` : ''}` +
          `${here === u.id ? ` ${styles.cursor}` : ''}`
        }
        style={{ left: n.x, top: n.y, width: n.w, height: n.h }}
        onClick={() => choose(u.id)}
      >
        <span className={styles.tick} style={{ background: headColour(u) }} />
        <span className={styles.ltx}>
          <span className={styles.u}>{u.name}</span>
          {detail === 'full' && (
            <span className={styles.o}>
              {headLabel(u)}
              {u.headRank ? ` · ${u.headRank}` : ''}
            </span>
          )}
        </span>
        {detail !== 'block' && (
          <span className={styles.cnt}>
            {detail === 'full' && <i>People</i>}
            {peopleLabel(model, u)}
          </span>
        )}
        {detail === 'full' && pin(u.id)}
      </div>
    );
  };

  const toggles = detail === 'block' ? [] : model.ids
    .filter((id) => model.byId[id].kids.length)
    .map((id) => {
      const n = layout.nodes[id];
      if (!n || n.leaf) return null;
      const isCollapsed = collapsed.has(id);
      const x = layout.mode === 'h' ? n.x + n.w - 6 : n.x + n.w / 2 - 14;
      const y = layout.mode === 'h' ? n.y + n.h / 2 - 10 : n.y + n.h - 10;
      return (
        <button
          key={`t-${id}`}
          type="button"
          className={`${styles.tg}`}
          style={{ left: x, top: y }}
          title={isCollapsed ? 'Show what reports to this unit' : 'Hide what reports to this unit'}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse(id);
          }}
        >
          {isCollapsed ? `+${model.descendants(id).length}` : '−'}
        </button>
      );
    });

  const sel = selected ? model.byId[selected] : null;

  return (
    <div
      className={styles.stage}
      ref={stageRef}
      role="tree"
      aria-label="Org structure chart"
      aria-activedescendant={here ? `orgnode-${here}` : undefined}
      tabIndex={0}
      onKeyDown={onStageKey}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* The way back. Once the chart is showing one line, this is the only
          thing on screen that still knows the rest of the tree exists. */}
      {sel && (
        <div className={`${styles.spotbar} ${styles.overlay}`}>
          <span className={styles.pulse} />
          <span className={styles.tx}>
            {only ? 'Showing the line of ' : 'Selected '}
            <b>{sel.name}</b> · {sel.path.length} above, {model.descendants(sel.id).length} below
            {only && view !== 'compact' ? ' · stacked to fit' : ''}
          </span>
          {onShowDetails && (
            <button aria-label="Open this unit's details" type="button" onClick={onShowDetails} title="Open this unit's details">
              Details
            </button>
          )}
          <button aria-label="Keep it in the compare set" type="button" onClick={() => onToggleCompare(sel.id)} title="Keep it in the compare set">
            <Icon name="cols" className={styles.ic} size={13} />
            {stored(sel.id) ? 'Stored ✓' : 'Add to compare'}
          </button>
          <button aria-label="Clear the selection and put the whole tree back" type="button" title="Clear the selection and put the whole tree back" onClick={() => onSelect(null)}>
            Show everything
          </button>
        </div>
      )}

      <div className={styles.layer} ref={layerRef}>
        <svg className={styles.wires}>
          {layout.links.map(({ from, to, leaf }) => (
            <path
              key={`w-${to.id}`}
              className={`${lit.has(to.id) ? styles.lit : ''}`}
              d={wirePath(from, to, layout.mode, leaf)}
            />
          ))}
        </svg>
        <div>
          {Object.values(layout.nodes).map((n) => (n.leaf ? leafRow(n) : card(n)))}
          {toggles}
        </div>
      </div>

      {hint && (
        <div className={`${styles.hint} ${styles.overlay}`}>
          <h4>Reading the chart</h4>
          <ul>
            <li>
              <kbd>click</kbd>
              <span>A unit to focus its line of command — everything else blurs back.</span>
            </li>
            <li>
              <kbd>drag</kbd>
              <span>Anywhere to pan. Scroll to zoom, or press Fit to frame it all.</span>
            </li>
            <li>
              <kbd>+</kbd>
              <span>On a card to store it, then Compare puts the stored ones side by side.</span>
            </li>
          </ul>
          <button aria-label="Dismiss this note for good" type="button" className={styles.hintgo} title="Dismiss this note for good" onClick={hint}>
            Got it
          </button>
        </div>
      )}

      {/* At these zooms the card is an abstraction, so say what it encodes —
          otherwise the chart just looks like it has stopped rendering. */}
      {(detail === 'block' || detail === 'mini') && (
        <p className={`${styles.lodnote} ${styles.overlay}`}>
          {detail === 'block' ? (
            <>
              <span className={styles.swatch} style={{ background: 'var(--org-l2)' }} />
              Colour shows the level
              <span className={styles.swatch} style={{ background: 'var(--org-danger)' }} />
              red means no officer in charge · zoom past 25% for names
            </>
          ) : (
            <>Names only at this zoom · zoom past 32% for officers and figures</>
          )}
        </p>
      )}

      <div className={`${styles.zoomer} ${styles.overlay}`}>
        <button type="button" onClick={() => zoomBy(1 / 1.2)} aria-label="Zoom out" title="Zoom out">
          −
        </button>
        <span className={styles.zval}>{zoomLabel}</span>
        <button type="button" onClick={() => zoomBy(1.2)} aria-label="Zoom in" title="Zoom in">
          +
        </button>
        <button aria-label="Frame the whole chart on screen" type="button" title="Frame the whole chart on screen" onClick={fit}>
          Fit
        </button>
      </div>

    </div>
  );
};

export default OrgChart;
